import { BuyerUI } from '@choco-admin/types';
import { useCallback, useState, useMemo, useEffect } from 'react';

import { usePuppyMode } from '../../../contexts/PuppyModeContext';
import {
  type BuyersPageSearchBuyersQuery,
  useBuyersPageSearchBuyersLazyQuery,
} from '../../../generated';
import { mapToBuyerUIList, type GeneratedBuyerFragment } from '../utils';

export type CaptureExceptionFunction = (error: Error) => void;

export type SearchBuyersParams = {
  searchTerm?: string;
  page?: number;
  pageSize?: number;
};

export type UseSearchBuyersResponse = {
  buyers: BuyerUI[];
  totalBuyers: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  hasNextPage: boolean;
  currentPage: number;
  totalPages: number;
  searchBuyers: (params: SearchBuyersParams) => Promise<void>;
  refetch: () => Promise<void>;
};

export type UseSearchBuyersConfig = {
  initialPageSize: number;
  captureException?: CaptureExceptionFunction;
};

type BuyerSearchResult = BuyersPageSearchBuyersQuery['search'];

interface SearchHit {
  entity: GeneratedBuyerFragment;
}

interface SearchResultData {
  hits?: SearchHit[] | null;
  total?: number | null;
}

const mapBuyers = (
  result: BuyerSearchResult | null | undefined
): { buyers: BuyerUI[]; total: number } => {
  if (!result) {
    return { buyers: [], total: 0 };
  }

  const searchResult = result as SearchResultData;
  const { hits, total } = searchResult;

  try {
    const buyerEntities = hits?.map((hit) => hit.entity) || [];
    const buyers = mapToBuyerUIList(buyerEntities);
    return { buyers, total: total || 0 };
  } catch (error) {
    // Return empty result on mapping error
    return { buyers: [], total: 0 };
  }
};

export const useSearchBuyers = (
  config: UseSearchBuyersConfig
): UseSearchBuyersResponse => {
  const { captureException, initialPageSize } = config;
  const { puppyFilter } = usePuppyMode();
  const [lastParams, setLastParams] = useState<SearchBuyersParams | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Last non-zero total per result set (search term + dogfooding mode). Page
  // responses can omit or zero the total, which used to make "Showing X of Y
  // buyers" (and the page count) lose Y after any pagination — keep the
  // last-known value instead (#109). Keyed by result set so another term or
  // mode never inherits it, and returning to one gets its own back.
  const [knownTotals, setKnownTotals] = useState<Record<string, number>>({});

  const [executeQuery, { data, variables, loading, error }] =
    useBuyersPageSearchBuyersLazyQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    });

  const searchBuyers = useCallback(
    async (params: SearchBuyersParams) => {
      try {
        setLastParams(params);
        const page = params.page || 1;
        const pageSize = params.pageSize || initialPageSize;
        setCurrentPage(page);

        await executeQuery({
          variables: {
            searchTerm: params.searchTerm || '',
            size: pageSize,
            offset: (page - 1) * pageSize,
            filter:
              puppyFilter !== undefined
                ? { buyer: { isPuppy: puppyFilter } }
                : undefined,
          },
        });
      } catch (err) {
        captureException?.(err as Error);
        throw err;
      }
    },
    [executeQuery, captureException, initialPageSize, puppyFilter]
  );

  const refetch = useCallback(async () => {
    if (lastParams) {
      await searchBuyers(lastParams);
    }
  }, [lastParams, searchBuyers]);

  const { buyers, total } = useMemo(
    () => mapBuyers(data?.search),
    [data?.search]
  );

  // Keyed by the variables the data was fetched with, so the total is never
  // filed under a result set the data does not belong to.
  const resultSetKey = variables
    ? `${variables.searchTerm}|${String(variables.filter?.buyer?.isPuppy)}`
    : null;

  useEffect(() => {
    if (resultSetKey !== null && total > 0) {
      setKnownTotals((known) =>
        known[resultSetKey] === total
          ? known
          : { ...known, [resultSetKey]: total }
      );
    }
  }, [resultSetKey, total]);

  // Later pages can omit or zero the total, so they fall back to the last-known
  // one (#109). A first page with no total is a genuine "no matches" — never
  // let a retained count hide it.
  const isLaterPage = (variables?.offset ?? 0) > 0;
  const effectiveTotal =
    total > 0
      ? total
      : (isLaterPage && resultSetKey && knownTotals[resultSetKey]) || 0;

  const totalPages = useMemo(() => {
    if (!effectiveTotal) return 0;
    const pageSize = lastParams?.pageSize ?? initialPageSize;
    return Math.ceil(effectiveTotal / pageSize);
  }, [effectiveTotal, lastParams?.pageSize, initialPageSize]);

  const hasNextPage = useMemo(() => {
    return currentPage < totalPages;
  }, [currentPage, totalPages]);

  return useMemo(
    () => ({
      buyers: buyers || [], // Ensure we always return an array
      totalBuyers: effectiveTotal,
      isLoading: loading,
      isFetching: loading,
      error: error || null,
      hasNextPage,
      currentPage,
      totalPages,
      searchBuyers,
      refetch,
    }),
    [
      buyers,
      effectiveTotal,
      loading,
      error,
      hasNextPage,
      currentPage,
      totalPages,
      searchBuyers,
      refetch,
    ]
  );
};
